<template>
  <div class="flex flex-column gap-3">
    <profile-time-off-filters :visible.sync="visibleFiltersModal" :current-filters.sync="filters" />

    <time-off-bulk :ids="selectedTimeOffs" @update="afterBulk" v-show="selectedTimeOffs.length" />

    <table-layout>
      <template #header>
        <h3 class="m-0">Time Off</h3>
      </template>

      <template #controls-right>
        <a-badge :count="countFilters">
          <a-button icon="filter" @click="visibleFiltersModal = true"> Filters </a-button>
        </a-badge>

        <a-button icon="undo" :disabled="!canResetFilters" @click="resetFilters"> Reset Filters </a-button>

        <a-button class="flex align-items-center justify-content-center" @click="fetchTimeOffs" title="Refresh Page">
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </template>

      <template #filters>
        <active-table-filters
          :items="activeFilterTags"
          @edit="openFilterEditor"
          @remove="removeFilter"
          @reset="resetFilters"
        />
      </template>

      <a-table
        size="small"
        :rowKey="record => record.id"
        :row-selection="{
          selectedRowKeys: selectedTimeOffs,
          onChange: onSelectChange,
          getCheckboxProps: record => {
            const { status, associate } = record

            const disabled =
              status === 1
                ? !hasUncontrolledPermission('manage_shifts', {
                    id: associate.id
                  })
                : status === 2
                  ? !hasAnyUncontrolledPermission(['validate_payments', 'process_payments'], { id: associate.id })
                  : true

            return { props: { disabled } }
          }
        }"
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :style="{ whiteSpace: 'pre' }"
        :pagination="pagination"
        :scroll="{ x: 840, y: tableScrollHeight }"
        :rowClassName="getRowClassName"
        @change="handleTableChange"
        :customRow="
          record => ({
            on: {
              click: () => handleOpenRequest(record.id)
            }
          })
        "
      >
        <span slot="status" slot-scope="text, record">
          <a-tag :color="getTimeOffStatus(record.status)?.color">{{
            getTimeOffStatus(record.status)?.title ?? ' - '
          }}</a-tag>
        </span>
        <span slot="type" slot-scope="text, record">
          <a-tag :color="getTimeOffType(record.type)?.color">{{ getTimeOffType(record.type)?.title ?? ' - ' }}</a-tag>
        </span>
        <span slot="actions" slot-scope="text, record">
          <button-more :record="record" :menu-items="menuItems" />
        </span>
      </a-table>
    </table-layout>
  </div>
</template>

<script>
import permissionMixin from '@/mixins/permissionMixin'
import { Badge, Button, notification, Table, Tag } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import tableMixin from '@/mixins/tableMixin'
import TimeOffBulk from '@/modules/time-off/components/time-off-bulk.vue'
import ProfileViewTimeOffFilters from './profile-view-time-off-filters.vue'

const {
  mapActions: timeOffAction,
  mapState: timeOffState,
  mapGetters: timeOffGetters
} = createNamespacedHelpers('timeOff')
const { mapActions: modalsAction } = createNamespacedHelpers('modals')
const { mapState: profileState } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-badge': Badge,
    'a-tag': Tag,
    'time-off-bulk': TimeOffBulk,
    'profile-time-off-filters': ProfileViewTimeOffFilters
  },
  data() {
    return {
      visibleFiltersModal: false,
      filters: {},
      columns: [
        {
          title: 'Requesting Dates',
          key: 'items',
          width: 240,
          customRender: (_, record) => {
            const { items } = record

            if (!items.length) {
              return '-'
            }

            const formattedDates = items.map(date => this.formatDateString(date.date))

            if (items.length === 1) {
              return formattedDates[0]
            }

            return `${formattedDates[0]} - ${formattedDates[items.length - 1]} (${items.length} dates)`
          },
          sorter: (a, b) => {
            const dateA = a.items?.length ? new Date(a.items[0].date) : null
            const dateB = b.items?.length ? new Date(b.items[0].date) : null

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          },
          originalIndex: 0
        },
        {
          title: 'Submission Date',
          key: 'created_at',
          width: 140,
          customRender: (_, record) => {
            return this.formatDateString(record.created_at)
          },
          sorter: (a, b) => {
            const dateA = new Date(a.created_at)
            const dateB = new Date(b.created_at)

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          },
          originalIndex: 1
        },
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
          width: 120,
          scopedSlots: {
            customRender: 'type'
          },
          sorter: (a, b) => a.type - b.type,
          originalIndex: 2
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 140,
          scopedSlots: {
            customRender: 'status'
          },
          sorter: (a, b) => a.status - b.status,
          originalIndex: 3
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 100,
          scopedSlots: {
            customRender: 'actions'
          },
          originalIndex: 4
        }
      ],

      tableData: [],
      selectedTimeOffs: [],

      loading: false
    }
  },
  mixins: [permissionMixin, tableMixin],
  watch: {
    filters: {
      handler(newVal, prevVal) {
        if (JSON.stringify(newVal) != JSON.stringify(prevVal)) {
          this.pagination.current = 1
          this.fetchTimeOffs()
        }
      },
      deep: true,
      immediate: true
    },
    'associate.id': {
      handler(newVal, prevVal) {
        if (newVal && newVal !== prevVal) {
          this.pagination.current = 1
          this.fetchTimeOffs()
        }
      },
      immediate: true
    },
    timeOffs: {
      handler(newVal) {
        this.pagination.total = newVal?.total_count ?? 0
        this.tableData = newVal?.items ?? []
      },
      deep: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    ...timeOffState({
      timeOffs: state => state.timeOffs
    }),
    ...timeOffGetters(['getTimeOffStatus', 'getTimeOffType']),

    timeZone() {
      return this.$store.state.applicationState.timeZone
    },

    menuItems() {
      return [
        {
          key: 'open-request',
          icon: 'fluent:open-20-regular',
          label: 'Open Request',
          visible: true,
          handler: record => {
            this.handleOpenRequest(record.id)
          }
        },
        {
          key: 'delete-time-off',
          icon: 'carbon:trash-can',
          label: 'Delete',
          visible: true,
          handler: record => {
            this.deleteTimeOffConfirm(record.id)
          }
        }
      ]
    }
  },
  methods: {
    ...timeOffAction(['getTimeOffs', 'deleteTimeOff']),
    ...modalsAction(['openModal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    onSelectChange(_, selectedRows) {
      this.selectedTimeOffs = selectedRows.map(item => item.id)
    },

    afterBulk() {
      this.selectedTimeOffs = []
      this.fetchTimeOffs()
    },

    resetFilters() {
      this.filters = {}
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.fetchTimeOffs()
    },

    formatDateString(date) {
      return moment.utc(date).tz(this.timeZone).format('MM/DD/YYYY')
    },

    handleOpenRequest(timeOffId) {
      this.openModal({
        type: 'timeOff',
        data: {
          visible: true,
          timeOffId,
          timeOffList: (this.timeOffs?.items ?? []).map(item => item.id),
          update: () => {
            this.fetchTimeOffs()
          }
        }
      })
    },

    async fetchTimeOffs() {
      if (!this.associate?.id) {
        return
      }

      const payload = {
        page: this.pagination.current,
        size: this.pagination.pageSize,
        ...this.filters,
        associates: [this.associate.id]
      }

      this.loading = true

      let skipFinally = false

      try {
        await this.getTimeOffs(payload)
      } catch (error) {
        if (error === 'canceled') {
          skipFinally = true
          this.loading = true
          return
        }

        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        if (!skipFinally) this.loading = false
      }
    },
    deleteTimeOffConfirm(id) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Delete Time Off',
          description: 'Are you sure you want to delete this time off?',
          onConfirm: async () => {
            await this.deleteTimeOff({ id })
            this.fetchTimeOffs()

            this.showNotification('success', 'Info', 'Time off has been deleted successfully')
          }
        }
      })
    }
  }
}
</script>
